/**
 * What a failed request says to the person in front of the screen.
 *
 * Every screen prints the server's own words in parentheses after its own
 * Czech sentence — "Členy se nepodařilo načíst. (…)". That was fine while the
 * words were only ever read by whoever was debugging. Then the rate limiter
 * started answering, and what appeared in the parentheses was "Too Many
 * Requests": English, in a Czech interface, on a screen that was working
 * perfectly, telling an operator nothing they could act on.
 *
 * This module imports nothing, so scripts/verify-web-filters.mjs can call the
 * real functions rather than a copy of them — and so the seconds-to-words
 * arithmetic lives in exactly one place. It has two callers that render it
 * differently (a parenthetical on six screens, a sentence on the login page),
 * and two copies of a rounding rule is how they end up disagreeing about
 * whether 91 seconds is "91 s" or "2 min".
 */

/**
 * Czech for the error codes an operator can actually meet.
 *
 * A code with no entry here falls through as itself. That is deliberate: an
 * untranslated `invalid_query` in the parentheses is ugly and tells whoever is
 * looking exactly what to search the source for, whereas a catch-all "Došlo k
 * chybě" would hide it. The list grows when a code turns out to be one the
 * staff meet, not when one is added to the server.
 *
 * Lower case and without a full stop, because these are fragments: the screen
 * supplies the sentence and the brackets around them.
 */
export const ERROR_MESSAGES: Record<string, string> = {
  too_many_requests: 'příliš mnoho požadavků',
  unauthorized: 'přihlášení vypršelo',
  account_disabled: 'účet je deaktivovaný',
  forbidden: 'nemáte oprávnění',
  forbidden_origin: 'požadavek nepřišel z této aplikace',
  action_disabled: 'tato akce se zatím provádí ve starém systému',
  // WhatsApp replies (30 Oct 2026, Codex F-04): the server's sentence is the one the desk should read.
  opted_out: 'zákazník odpověděl STOP — přes WhatsApp mu už nepíšeme, zavolejte nebo napište e-mail',
  window_closed: 'zákazník nenapsal za posledních 24 hodin — WhatsApp dovolí odpovědět jen do 24 h, zavolejte nebo napište e-mail',
  whatsapp_not_sending: 'odesílání přes WhatsApp je vypnuté (režim bez odesílání)',
  not_found: 'záznam neexistuje',
  uncertain: 'spojení se přerušilo — změna se mohla uložit; obnovte stránku a zkontrolujte, opakované stisknutí ji neprovede dvakrát',
  db_unreachable: 'databáze neodpovídá, nic se neuložilo — zkuste to za chvíli',
  op_id_required: 'aplikace se mezitím aktualizovala — obnovte stránku (F5) a akci zopakujte',
  op_id_reused: 'tato akce koliduje s jinou — obnovte stránku a zopakujte ji',
  replayed_secret: 'akce proběhla, ale heslo se cestou ztratilo — vygenerujte ho znovu',
  op_unverifiable: 'toto číslo akce patří starší akci, kterou nelze ověřit — obnovte stránku, zkontrolujte výsledek a případně akci zopakujte',
  replayed_refresh: 'akce už proběhla — obnovte stránku (F5) a zkontrolujte výsledek',
  bad_reply: 'odpověď serveru se nedala přečíst — zkuste to znovu',
};

/**
 * The wait, in words a person reads rather than a number to be converted.
 *
 * This is the only part of a refusal anybody can do anything with: the
 * difference between waiting and telephoning the office is knowing it is forty
 * seconds. Above ninety it goes to whole minutes — "za 154 s" is arithmetic
 * homework, not an answer — and rounds up, because a hint that expires before
 * the limit does sends the person back to a second refusal.
 *
 * Returns '' when there is no usable number, which is the caller's signal to
 * say nothing rather than to say "za 0 s".
 */
export function retryHint(seconds: unknown): string {
  if (typeof seconds !== 'number' || !Number.isFinite(seconds) || seconds <= 0) return '';
  if (seconds <= 90) return `za ${Math.ceil(seconds)} s`;
  return `za ${Math.ceil(seconds / 60)} min`;
}

/**
 * The fragment the six screens put in their parentheses.
 *
 * The wait is appended only for the one code where it means something. A
 * `retryAfter` on any other error would be the server saying something the
 * client should not be inventing a sentence around.
 */
export function apiErrorMessage(code: string, retryAfter?: unknown): string {
  const text = ERROR_MESSAGES[code] ?? code;
  if (code !== 'too_many_requests') return text;
  const hint = retryHint(retryAfter);
  return hint ? `${text}, zkuste to ${hint}` : text;
}
